import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApplicationStore } from '../home/ApplicationStore';
import userProfileStore from '../core/UserProfileStore';
import { useIPRestrictionStore } from '../dealer-admin/ip-access/IPRestrictionStore';
import { IUserIPRestrictionEntity } from '../dealer-admin/ip-access/IPRestrictionInterface';
import { useAnalytics, whenStartupTracked } from '../third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { setDocumentTitle } from '../../util/TitleManager';
import { useStartupWorkflowStore } from '../startup-app/StartupWorkflowStore';
import { useStartupDriveStore } from '../startup-app/StartupDriveStore';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';

const STARTUP_REDIRECT_KEY = 'unify-startup-redirect-done';
const DRIVE_APP_ID = 'DMSDRIVE';

export default function StartupAppRedirect() {
  const [done, setDone] = useState(false);
  const history = useHistory();
  const { applications } = useApplicationStore();
  const { unifyUserProfile } = userProfileStore();
  const ipRestriction = useIPRestrictionStore((state: any) => state.ipRestriction);
  const { trackEvent } = useAnalytics();
  const setStartupWorkflowUrl = useStartupWorkflowStore((state) => state.setStartupWorkflowUrl);
  const setShouldLaunchDrive = useStartupDriveStore((state) => state.setShouldLaunchDrive);
  const startupAppFlag = useFeatureFlag('UNIFY_STARTUP_APP');

  useEffect(() => {
    if (done) return;
    if (startupAppFlag === undefined) return; // SDK still loading — wait
    if (startupAppFlag === false) {
      setDone(true); // explicitly off — lock in for session
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }
    if (!applications?.length) return;
    if (!unifyUserProfile?.apps || !Array.isArray(unifyUserProfile.apps)) return;
    if (!Array.isArray(ipRestriction)) return;

    if (localStorage.getItem(STARTUP_REDIRECT_KEY) === 'true') {
      setDone(true);
      return;
    }

    const adminPath = /^\/unify\/(unify-admin|dealer-admin|global-search)/;
    if (adminPath.test(window.location.pathname)) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    // Check for default app first
    const defaultApp = unifyUserProfile.apps.find(
      (app: any) => app.isDefaultApp === true
    );

    if (!defaultApp) {
      // No default app — check for default workflow
      const defaultWorkflow = unifyUserProfile.workflows?.find(
        (wf: any) => wf.isDefaultWorkflow === true
      );

      if (!defaultWorkflow) {
        setDone(true);
        localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
        return;
      }

      const workflowId = defaultWorkflow.workflowId;
      const appId = defaultWorkflow.appId;

      const appRestrictionEntry = ipRestriction.find(
        (item: IUserIPRestrictionEntity) =>
          item.entityType === 'application' &&
          item.entityId === appId
      );

      let isRestricted: boolean;
      if (appRestrictionEntry?.isRestricted) {
        isRestricted = true;
      } else if (appRestrictionEntry?.isIpConfigured) {
        isRestricted = false;
      } else {
        isRestricted = ipRestriction.some(
          (item: IUserIPRestrictionEntity) =>
            item.entityType === 'workflow' &&
            item.entityId === workflowId &&
            item.isRestricted
        );
      }

      if (isRestricted) {
        setDone(true);
        localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
        return;
      }

      // Find workflow details from applications store
      const parentApp = applications.find((app: any) => app.id === appId);
      const workflowDetails = parentApp?.workflows?.find(
        (wf: any) => wf.workflowId === workflowId || wf.id === workflowId
      );

      if (!workflowDetails?.url || !workflowDetails.url.startsWith('http')) {
        setDone(true);
        localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
        return;
      }

      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      setTimeout(() => {
        setDocumentTitle(workflowDetails.name, workflowDetails.url);
        // Launch now, but send the event after the startup events so it doesn't land between them.
        whenStartupTracked().then(() => trackEvent(AMPLITUDE_EVENTS.OPEN_WORKFLOW, { Workflow: workflowDetails.name }));
        setStartupWorkflowUrl(workflowDetails.url);
        history.push(`/unify/wview/${appId}/${workflowId}/N`);
      }, 500);
      return;
    }

    // Default app found — proceed with app redirect
    const appId = defaultApp.appId;

    // Skip if app is IP restricted
    const isRestricted = ipRestriction.some(
      (item: IUserIPRestrictionEntity) =>
        item.entityType === 'application' &&
        item.entityId === appId &&
        item.isRestricted
    );

    if (isRestricted) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    const appDetails = applications.find((app: any) => app.id === appId);

    if (!appDetails) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    if (appId === DRIVE_APP_ID) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      setShouldLaunchDrive(true);
      return;
    }

    const appUrl = appDetails.url || '';
    if (!appUrl || !appUrl.startsWith('http')) {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    const urlParams = new URLSearchParams(appUrl.split('?')[1]);
    if (urlParams.get('launchtype') === 'tab') {
      setDone(true);
      localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
      return;
    }

    setDone(true);
    localStorage.setItem(STARTUP_REDIRECT_KEY, 'true');
    setTimeout(() => {
      setDocumentTitle(appDetails.name, appUrl);
      // Launch now, but send the event after the startup events so it doesn't land between them.
      whenStartupTracked().then(() => trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, { Application: appDetails.name }));
      history.push(`/unify/aview/${appId}/N`);
    }, 500);
  }, [done, applications, unifyUserProfile, ipRestriction, history, trackEvent, setStartupWorkflowUrl, setShouldLaunchDrive, startupAppFlag]);

  return null;
}